import React from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { FaCheckCircle, FaCircle, FaClock, FaExclamationCircle, FaMinus } from 'react-icons/fa';
import { format, parseISO } from 'date-fns';
import type { PortalTask, TaskStatus } from '../types';

const PROPERTY_PRIMARY = '#0D7F6E';

const statusConfig: Record<TaskStatus, { icon: React.ElementType; color: string; label: string }> = {
  COMPLETE: { icon: FaCheckCircle, color: 'green.500', label: 'Complete' },
  IN_PROGRESS: { icon: FaClock, color: PROPERTY_PRIMARY, label: 'In Progress' },
  AWAITING_CLIENT: { icon: FaExclamationCircle, color: 'orange.400', label: 'Action Required' },
  PENDING: { icon: FaCircle, color: 'gray.300', label: 'Upcoming' },
  SKIPPED: { icon: FaMinus, color: 'gray.300', label: 'Skipped' },
};

interface PhaseTaskTimelineProps {
  tasks: PortalTask[];
}

export const PhaseTaskTimeline: React.FC<PhaseTaskTimelineProps> = ({ tasks }) => {
  return (
    <VStack spacing={0} align="stretch">
      {tasks.map((task, idx) => {
        const cfg = statusConfig[task.status];
        const isLast = idx === tasks.length - 1;
        return (
          <HStack key={task.id} spacing={0} align="stretch">
            {/* Vertical connector column */}
            <Box display="flex" flexDirection="column" alignItems="center" mr={3} minW="20px">
              <Icon
                as={cfg.icon}
                boxSize={5}
                color={cfg.color}
                mt="2px"
                flexShrink={0}
              />
              {!isLast && (
                <Box
                  flex={1}
                  w="2px"
                  bg={task.status === 'COMPLETE' ? 'green.200' : 'gray.200'}
                  my={1}
                  minH="16px"
                />
              )}
            </Box>
            {/* Task content */}
            <Box pb={isLast ? 0 : 4} flex={1}>
              <HStack spacing={2} flexWrap="wrap">
                <Text
                  fontSize="sm"
                  fontWeight={task.status === 'AWAITING_CLIENT' ? 'semibold' : 'normal'}
                  color={
                    task.status === 'COMPLETE'
                      ? 'gray.500'
                      : task.status === 'AWAITING_CLIENT'
                      ? 'orange.700'
                      : 'gray.800'
                  }
                  textDecoration={task.status === 'COMPLETE' ? 'line-through' : 'none'}
                >
                  {task.title}
                </Text>
                {task.status === 'AWAITING_CLIENT' && (
                  <Badge colorScheme="orange" fontSize="xs" borderRadius="full">
                    Action Required
                  </Badge>
                )}
              </HStack>
              {task.dueDate && task.status !== 'COMPLETE' && (
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  Due {format(parseISO(task.dueDate), 'd MMM yyyy')}
                </Text>
              )}
              {task.assignedToName && task.status !== 'COMPLETE' && task.status !== 'PENDING' && (
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  {task.assignedToName}
                </Text>
              )}
            </Box>
          </HStack>
        );
      })}
    </VStack>
  );
};
